import { useEffect } from "react";
import { useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import { getItemById, typeBadgeClass, typeLabels, formatDate } from "@/utils/content";
import { usePageMeta } from "@/hooks/usePageMeta";
import ShareRow from "@/pages/detail/components/ShareRow";
import RelatedPanel from "@/pages/detail/components/RelatedPanel";
import CharacterRelations from "@/pages/detail/components/CharacterRelations";

export default function Detail() {
  const { id = "" } = useParams();
  const item = getItemById(id);

  usePageMeta(
    item ? `${item.title} - 年轻的母亲3` : "内容不存在 - 年轻的母亲3",
    item ? item.summary : undefined,
  );

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [id]);

  if (!item) {
    return (
      <div className="w-full px-4 md:px-8 py-16 md:py-24">
        <div className="max-w-[1100px] mx-auto">
          <EmptyState
            title="内容不存在"
            desc="你要查看的内容可能已被移除或链接有误，返回首页继续浏览《年轻的母亲3》的其他内容吧。"
            to="/"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="w-full px-4 md:px-8 py-8 md:py-10">
      <div className="max-w-[1100px] mx-auto">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: typeLabels[item.type], to: `/category/${item.type}` },
            { label: item.subtitle || item.title },
          ]}
        />

        <article className="mt-5">
          <header className="mb-6 md:mb-8">
            <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${typeBadgeClass[item.type]}`}>
              {typeLabels[item.type]}
            </span>
            <h1 className="mt-3 font-heading font-bold text-2xl md:text-4xl text-foreground-950 leading-tight">
              {item.title}
            </h1>
            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-calendar-line"></i></span>
                {formatDate(item.date)}
              </span>
              {item.chapter !== undefined && (
                <span className="flex items-center gap-1.5">
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-play-list-line"></i></span>
                  第 {item.chapter} 集
                </span>
              )}
              {item.role && (
                <span className="flex items-center gap-1.5 text-accent-700">
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-user-star-line"></i></span>
                  {item.role}
                </span>
              )}
            </div>
          </header>

          <div className="w-full aspect-[16/9] rounded-lg overflow-hidden bg-background-200 mb-8">
            <img
              src={item.cover}
              alt={item.title}
              title={`${item.title} - 年轻的母亲3`}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-10">
            <div className="min-w-0">
              <p className="text-base md:text-lg text-foreground-800 leading-loose font-medium">
                {item.summary}
              </p>

              <div className="mt-8 space-y-8">
                {item.sections.map((section) => (
                  <section key={section.heading}>
                    <h2 className="font-heading font-bold text-lg md:text-xl text-foreground-950 flex items-center gap-2.5 mb-4">
                      <span className="w-1 h-5 rounded-full bg-accent-500"></span>
                      {section.heading}
                    </h2>
                    <div className="space-y-4">
                      {section.paragraphs.map((para, idx) => (
                        <p key={idx} className="text-[15px] text-foreground-700 leading-loose">
                          {para}
                        </p>
                      ))}
                    </div>
                  </section>
                ))}
              </div>

              {item.type === "character" && <CharacterRelations current={item} />}

              <ShareRow type={item.type} />
            </div>

            <RelatedPanel item={item} />
          </div>
        </article>
      </div>
    </div>
  );
}